<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.driver.tourList.editTour.title') }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <div class="flex gap-x-5 w-full mt-7.5">
        <Multiselect
          v-model="rawTour.campaignRegistration"
          :error="errors?.campaignRegistration"
          :options="campaignRegistrations"
          :label="$t('pages.driver.tourList.editTour.fields.campaign')"
          class="w-full"
          :can-deselect="true"
          :can-clear="true"
          :searchable="true"
        />
      </div>
      <div class="w-full text-center">
        <custom-button variant="primary" class="mt-15" :busy="loading" @click="saveNewTour">
          {{ $t('save') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="tsx" setup>
import {computed, reactive, ref} from 'vue';
import CustomButton from '@components/utils/CustomButton.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import {useVModel} from '@vueuse/core';
import {useI18n} from 'vue-i18n';
import {collOptionsProvider} from '@utils/table/hooks/provider';
import {useValidation} from '@hooks/form';
import {date, number, object, string} from 'yup';
import ModalHeader from '@components/utils/Modal/ModalHeader.vue';
import Modal from '@/components/utils/Modal/Modal.vue';
import {useNotify} from '@/components/utils/notifications';
import type {CampaignRegistration, Driver, Tour} from '@/entities/Entities';
import {useFetchAction} from '@/hooks/fetch';
import {syncRef} from '@hooks/state';

interface Props {
  open: boolean;
  driver: Driver;
  tour: Tour;
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalTour = useVModel(props, 'tour', emit);

const {t} = useI18n();

const driverFilter = computed(() => ({driver: props.driver?.id ?? ''}));

const params = reactive({
  filter: driverFilter,
});

const icons = {
  km: <span>Km</span>,
};

const campaignRegistrations = collOptionsProvider<CampaignRegistration>(
  '/campaign_registrations',
  params,
  (value) => ({
    label: value.campaign.group.name,
    value: value['@id'],
  })
);

const notify = useNotify();

const driver = computed(() => props.driver);

const initialData = (tour?: Tour) => tour ? {
  campaignRegistration: tour.campaignRegistration?.['@id'] ?? undefined,
} : {
  driver,
  createdAt: undefined,
  distance: undefined,
  time: undefined,
  campaignRegistration: undefined,
};

const rawTour = syncRef(internalTour, (tour) => ({
  ...initialData(tour),
}));

const {
  data: tour,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    campaignRegistration: string().nullable(),
  }),
  rawTour
);

function resetData() {
  rawTour.value = {...initialData()} as unknown as typeof rawTour['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, {loading}] = useFetchAction();

const saveNewTour = validatedFn(async () => {
  try {
    await exec(`/tours/${props.tour.id}`, {
      method: 'PUT',
      body: {
        campaignRegistration: tour.value.campaignRegistration,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.driver.tourList.editTour.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.tourList.editTour.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
